import { Flex, Skeleton, Stack } from '@mantine/core';
import type { AgentCostsSummary } from '../../../../api/events-client/events-client.type';
import { OverviewCard } from '../../../../components/overview-card/overview-card';
import { formatCurrency, getCurrency } from '../../../../helpers/currency-helpers';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { skyDark } from '../../../../styles/design-tokens';
import { Text } from '../../../../ui-library/typography/typography';

type AgentCostsOverviewProps = {
  summary?: AgentCostsSummary;
  isPending: boolean;
};

function AgentCostsOverview({ summary, isPending }: AgentCostsOverviewProps) {
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode } = getCurrency(organization);

  if (isPending) {
    return (
      <Flex gap={16} wrap="wrap">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} height={120} style={{ flex: '1 1 140px' }} radius={8} />
        ))}
      </Flex>
    );
  }

  const cards = [
    {
      title: 'Total Cost',
      icon: 'payments',
      value: formatCurrency(summary?.total_cost ?? 0, { currency: currencyCode }),
      description: 'Across all sessions',
    },
    {
      title: 'Total Tokens',
      icon: 'token',
      value: (summary?.total_tokens ?? 0).toLocaleString(),
      description: 'Input + output + cache',
    },
    {
      title: 'Cost per Session',
      icon: 'attach_money',
      value: formatCurrency(summary?.median_cost_per_session ?? 0, { currency: currencyCode }),
      description: 'Median across sessions',
    },
    {
      title: 'Active Users',
      icon: 'group',
      value: String(summary?.active_users ?? 0),
      description: 'Distinct users',
    },
    {
      title: 'Total Sessions',
      icon: 'forum',
      value: String(summary?.total_sessions ?? 0),
      description: 'Agent sessions',
    },
  ];

  return (
    <Flex gap={16} wrap="wrap">
      {cards.map((card) => (
        <OverviewCard
          key={card.title}
          title={card.title}
          icon={card.icon}
          content={
            <Stack align="center" justify="center" style={{ minHeight: 80 }}>
              <Text size="large" weight="bold">
                {card.value}
              </Text>
              <Text size="small" color={skyDark}>
                {card.description}
              </Text>
            </Stack>
          }
        />
      ))}
    </Flex>
  );
}

export { AgentCostsOverview };
